/**
 * El Torito boot record parser.
 *
 * Boot Record Volume Descriptor sits at sector 17 (offset 0x8800), type 0x00,
 * standard ID "CD001", boot system ID "EL TORITO SPECIFICATION".
 *
 * The descriptor points (at offset 71) to the LBA of the Boot Catalog. The
 * catalog contains:
 *   - One Validation Entry (32 bytes, byte 0 = 0x01)
 *   - One Default Initial/Default Entry (32 bytes, byte 0 = 0x88 if bootable)
 *   - Optionally Section Header / Section entries for additional images
 *     (header byte 0 = 0x90 final or 0x91 not-final)
 *
 * Each boot entry includes:
 *   byte 0: Boot indicator (0x88 = bootable, 0x00 = non-bootable)
 *   byte 1: Boot media type (0=no-emulation, 1=1.2MB FD, 2=1.44MB FD, 3=2.88MB FD, 4=hard disk)
 *   bytes 2-3: Load segment (real-mode segment for floppy/hd; 0=use default)
 *   byte 4: System type (partition table type)
 *   bytes 6-7: Sector count (number of 512-byte sectors to load)
 *   bytes 8-11: Image LBA
 *
 * Surface: window.__DiskImage.parseBoot(reader, brLba?) → Promise<{
 *   bootable: bool,
 *   catalogLba?: number,
 *   platforms: string[],     // 'BIOS' | 'EFI' | 'PowerPC' | 'Mac'
 *   bootImages: BootImage[],
 *   raw: { brSector, validation, defaultEntry, sections }
 * }>
 */
(function (w) {
    'use strict';
    w.__DiskImage = w.__DiskImage || {};
    if (w.__DiskImage.parseBoot) return;

    var SECTOR = 2048;

    function _u16le(b, off) { return b[off] | (b[off + 1] << 8); }
    function _u32le(b, off) { return ((b[off] | (b[off + 1] << 8) | (b[off + 2] << 16) | (b[off + 3] << 24)) >>> 0); }

    function _ascii(b, off, len) {
        var s = '';
        for (var i = 0; i < len; i++) {
            var c = b[off + i];
            if (c === 0) break;
            s += String.fromCharCode(c);
        }
        return s.replace(/\s+$/, '');
    }

    function _platformFromId(id) {
        switch (id) {
            case 0x00: return 'BIOS';
            case 0x01: return 'PowerPC';
            case 0x02: return 'Mac';
            case 0xEF: return 'EFI';
            default: return 'Unknown(0x' + id.toString(16) + ')';
        }
    }

    function _mediaName(t) {
        switch (t & 0x0F) {
            case 0: return 'no-emulation';
            case 1: return 'floppy-1.2MB';
            case 2: return 'floppy-1.44MB';
            case 3: return 'floppy-2.88MB';
            case 4: return 'hard-disk';
            default: return 'reserved-' + t;
        }
    }

    function _parseEntry(b, off, platform) {
        var bootInd = b[off];
        var media = b[off + 1];
        var loadSeg = _u16le(b, off + 2);
        var sysType = b[off + 4];
        var sectorCount = _u16le(b, off + 6);
        var imageLba = _u32le(b, off + 8);
        return {
            bootable: bootInd === 0x88,
            mediaType: _mediaName(media),
            mediaTypeRaw: media & 0x0F,
            loadSegment: loadSeg,
            partitionType: sysType,
            sectorCount: sectorCount,
            imageLba: imageLba,
            imageSize: sectorCount * 512,
            platform: platform
        };
    }

    function parseBoot(reader, brLbaHint) {
        // Find boot record VD at sector 17 (or scan if hint missing)
        var brP = brLbaHint != null
            ? reader.bytes(brLbaHint * SECTOR, SECTOR)
            : reader.bytes(17 * SECTOR, SECTOR);

        return brP.then(function (br) {
            if (br[0] !== 0x00) {
                // Scan VRS sectors 16..32 for type-0 BR
                return _scanBr(reader);
            }
            // Verify "EL TORITO SPECIFICATION" at bytes 7..38
            var bootSysId = _ascii(br, 7, 32);
            if (bootSysId.indexOf('EL TORITO') !== 0) {
                return _scanBr(reader);
            }
            return br;
        }).then(function (br) {
            if (!br) return { bootable: false, platforms: [], bootImages: [], raw: null };
            var catalogLba = _u32le(br, 71);
            return reader.bytes(catalogLba * SECTOR, SECTOR).then(function (cat) {
                var validation = {
                    headerId: cat[0],
                    platformId: cat[1],
                    manufacturer: _ascii(cat, 4, 24),
                    checksum: _u16le(cat, 28),
                    keyBytes: [cat[30], cat[31]]
                };
                var entries = [];
                var sections = [];
                var defaultPlat = _platformFromId(validation.platformId);
                var defaultEntry = _parseEntry(cat, 32, defaultPlat);
                entries.push(defaultEntry);

                // Section parsing (after default entry, starting at offset 64)
                var off = 64;
                while (off + 32 <= cat.length) {
                    var ind = cat[off];
                    if (ind !== 0x90 && ind !== 0x91) break;
                    var sectionPlat = _platformFromId(cat[off + 1]);
                    var nEntries = _u16le(cat, off + 2);
                    var sectionId = _ascii(cat, off + 4, 28);
                    sections.push({ platform: sectionPlat, count: nEntries, id: sectionId });
                    off += 32;
                    for (var i = 0; i < nEntries; i++) {
                        if (off + 32 > cat.length) break;
                        var e = _parseEntry(cat, off, sectionPlat);
                        entries.push(e);
                        off += 32;
                    }
                    if (ind === 0x90) break; // final
                }

                var platforms = entries.map(function (e) { return e.platform; }).filter(function (p, i, arr) { return arr.indexOf(p) === i; });
                var anyBootable = entries.some(function (e) { return e.bootable; });

                return {
                    bootable: anyBootable,
                    catalogLba: catalogLba,
                    platforms: platforms,
                    bootImages: entries,
                    raw: {
                        brSector: br,
                        validation: validation,
                        defaultEntry: defaultEntry,
                        sections: sections
                    }
                };
            });
        }).catch(function () {
            return { bootable: false, platforms: [], bootImages: [], raw: null };
        });
    }

    function _scanBr(reader) {
        // Walk VRS sectors and look for type 0 with EL TORITO sysid
        function step(lba) {
            if (lba > 32) return null;
            return reader.bytes(lba * SECTOR, SECTOR).then(function (s) {
                if (s[0] === 0xFF) return null;
                if (s[0] === 0x00 && _ascii(s, 1, 5) === 'CD001') {
                    var sysId = _ascii(s, 7, 32);
                    if (sysId.indexOf('EL TORITO') === 0) return s;
                }
                return step(lba + 1);
            }).catch(function () { return null; });
        }
        return step(16);
    }

    /**
     * Extract a boot image as a Blob (for 'extract boot image' UI).
     */
    function extractBootImage(reader, bootImage) {
        if (!bootImage || !bootImage.imageLba || bootImage.sectorCount === 0) {
            return Promise.reject(new Error('extractBootImage: no image LBA / size'));
        }
        var off = bootImage.imageLba * SECTOR;
        var len = bootImage.sectorCount * 512;
        // For floppy emulation 1.44MB the image is 1474560 bytes regardless of sectorCount field
        if (bootImage.mediaTypeRaw === 2 && len < 1474560) len = 1474560;
        if (bootImage.mediaTypeRaw === 1 && len < 1228800) len = 1228800;
        if (bootImage.mediaTypeRaw === 3 && len < 2949120) len = 2949120;
        return reader.bytes(off, Math.min(len, reader.size - off)).then(function (b) {
            return new Blob([b], { type: 'application/octet-stream' });
        });
    }

    w.__DiskImage.parseBoot = parseBoot;
    w.__DiskImage.extractBootImage = extractBootImage;
})(window);
